import React from 'react';

type SkeletonAnimation = 'pulse' | 'wave' | 'none';

interface SkeletonBenchmarksTableProps {
  columns?: number; // Number of assessment columns (e.g. LNF, PSF, NWF-CLS, NWF-WRC, WRF, Composite)
  animation?: SkeletonAnimation;
  hasFilters?: boolean; // Show/hide Grade/School filter row
  hasLegend?: boolean; // Show/hide the legend + generate PDF row
}

export const SkeletonBenchmarksTable: React.FC<SkeletonBenchmarksTableProps> = ({
  columns = 6,
  animation = 'wave',
  hasFilters = true,
  hasLegend = true,
}) => {
  const columnIndexes = Array.from({ length: columns });
  const gridTemplateColumns = `1.4fr ${'1fr '.repeat(columns).trim()}`;

  return (
    <div className="skeleton-benchmarks-table-wrapper">
      <div className="line skeleton-benchmarks-table-title" />

      {hasFilters && (
        <div className="skeleton-benchmarks-table-filters">
          <div className="skeleton-benchmarks-table-filter-group">
            <div className="line skeleton-benchmarks-table-filter-label" />
            <div className="line skeleton-benchmarks-table-filter-input" />
          </div>
          <div className="skeleton-benchmarks-table-filter-group">
            <div className="line skeleton-benchmarks-table-filter-label" />
            <div className="line skeleton-benchmarks-table-filter-input" />
          </div>
        </div>
      )}

      {hasLegend && (
        <div className="skeleton-benchmarks-table-legend">
          <div className="skeleton-benchmarks-table-legend-item">
            <div className="skeleton-benchmarks-table-legend-swatch skeleton-benchmarks-table-legend-swatch-below" />
            <div className="line skeleton-benchmarks-table-legend-text" />
          </div>
          <div className="skeleton-benchmarks-table-legend-item">
            <div className="skeleton-benchmarks-table-legend-swatch skeleton-benchmarks-table-legend-swatch-above" />
            <div className="line skeleton-benchmarks-table-legend-text" />
          </div>
          <div className="line skeleton-benchmarks-table-pdf-button" />
        </div>
      )}

      <div className="table-container">
        <div className="skeleton-table skeleton-assessment-table skeleton-assessment-table-grid skeleton-benchmarks-table">
          {/* Header row */}
          <div className="skeleton-assessment-table-row skeleton-benchmarks-table-header-row" style={{ gridTemplateColumns }}>
            <div className="skeleton-assessment-table-cell" />
            {columnIndexes.map((_, colIndex) => (
              <div key={`benchmark-header-${colIndex}`} className="skeleton-assessment-table-cell">
                <div className="line" style={{ width: '70px' }} />
              </div>
            ))}
          </div>

          {/* Well Below/Below row */}
          <div className="skeleton-assessment-table-row skeleton-benchmarks-table-row-below" style={{ gridTemplateColumns }}>
            <div className="skeleton-assessment-table-cell skeleton-benchmarks-table-row-label">
              <div className="line" style={{ width: '90px' }} />
            </div>
            {columnIndexes.map((_, colIndex) => (
              <div key={`below-cell-${colIndex}`} className="skeleton-assessment-table-cell">
                <div className="line" style={{ width: '60px' }} />
              </div>
            ))}
          </div>

          {/* At/Above row */}
          <div className="skeleton-assessment-table-row skeleton-benchmarks-table-row-above" style={{ gridTemplateColumns }}>
            <div className="skeleton-assessment-table-cell skeleton-benchmarks-table-row-label" />
            {columnIndexes.map((_, colIndex) => (
              <div key={`above-cell-${colIndex}`} className="skeleton-assessment-table-cell">
                <div className="line" style={{ width: '60px' }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SkeletonBenchmarksTable;
